import React from 'react'
import styled from 'styled-components'
import { Container, Flex, FlexProps, Hide } from 'pcln-design-system'
import CalendarCard from '../CalendarCard'
import Month from '../Month'
import Footer from '../Footer'
import useCalendar from '../useCalendar'

type CalendarFlexProps = FlexProps & {
  hasBoxShadow?: boolean
}

const LeftAlignedContainer = styled(Container)`
  width: auto;
  margin-left: unset;
  margin-right: unset;
  z-index: 4;
`

const CalendarFlex = styled<React.FC<CalendarFlexProps>>(Flex)`
  position: relative;
  z-index: 4;
  ${(props) => props.theme.mediaQueries[1]} {
    box-shadow: ${(props) => (props.hasBoxShadow ? props.theme.shadows['overlay-lg'] : 'none')};
  }
`

/** @public */
function CalendarRoot({ className }: { className?: string }) {
  const {
    animateScroll,
    boxShadow,
    calendarRef,
    calendarWrapperStyles,
    dayWrapperStyleOverride,
    didUseKeyNavigation,
    displayYear,
    endDate,
    firstValidMonth,
    footer,
    highlightedDates,
    hoveredDate,
    isMobile,
    locale,
    maxDate,
    maxSelectableDate,
    minDate,
    monthIndex,
    months,
    monthsToShow,
    monthWidth,
    roundedCorners = true,
    scrollToMonth,
    selectTwoDates,
    showHoveredDates = typeof window !== 'undefined' && window.innerWidth > 640,
    startDate,
    useDefaultFooter,
    handleKeyNav,
    onDayClicked,
    onDayHovered,
    onDismiss,
    onDisplayMonthsChange,
    setSelectedDayRef,
  } = useCalendar()

  const isTouch = document ? 'ontouchstart' in document.documentElement : true
  const hasFooterBoxShadow = boxShadow && footer ? boxShadow : null

  return (
    <LeftAlignedContainer
      className={className}
      maxWidth={monthWidth * monthsToShow}
      data-testid='calendar-root'
    >
      <CalendarFlex
        borderRadius={roundedCorners ? '2xl' : undefined}
        flexDirection='column-reverse'
        hasBoxShadow={hasFooterBoxShadow}
      >
        <Hide xs sm>
          <Flex width={[monthsToShow * monthWidth]}>
            {footer &&
              !useDefaultFooter &&
              React.cloneElement(footer, {
                dismissCalendar: onDismiss || undefined,
                roundedCorners: roundedCorners || undefined,
              })}
            {useDefaultFooter && <Footer roundedCorners={roundedCorners} dismissCalendar={onDismiss} />}
          </Flex>
        </Hide>
        <CalendarCard
          animateScroll={animateScroll}
          boxShadow={boxShadow && !footer ? boxShadow : null}
          calendarWrapperStyles={calendarWrapperStyles}
          firstValidMonth={firstValidMonth}
          hoveredDate={hoveredDate}
          maxSelectableDate={maxSelectableDate}
          monthsToShow={monthsToShow}
          monthWidth={monthWidth}
          roundedCorners={roundedCorners}
          scrollToMonth={scrollToMonth}
          keyNavHandler={handleKeyNav}
          onDisplayMonthsChange={onDisplayMonthsChange}
          onDismiss={onDismiss}
          setCalendarRef={calendarRef}
        >
          {months.map((month, i) => (
            <Month
              {...month}
              endDate={endDate}
              hoveredDate={hoveredDate}
              highlightedDates={highlightedDates}
              maxDate={maxDate}
              onDayClicked={onDayClicked}
              onDayHovered={onDayHovered}
              startDate={startDate}
              locale={locale}
              isMobile={isMobile}
              currentMonthIndex={monthIndex}
              monthsToShow={monthsToShow}
              minDate={minDate}
              maxSelectableDate={maxSelectableDate}
              dayWrapperStyleOverride={dayWrapperStyleOverride}
              showHoveredDates={selectTwoDates || showHoveredDates}
              didUseKeyNavigation={didUseKeyNavigation}
              monthWidth={monthWidth}
              displayYear={displayYear}
              isTouch={isTouch}
              setSelectedDayRef={setSelectedDayRef}
              index={i}
              key={i}
            />
          ))}
        </CalendarCard>
      </CalendarFlex>
    </LeftAlignedContainer>
  )
}

export default CalendarRoot
